import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { ApiError } from '@/lib/api';
import { defaultRendition, fetchSavedAssets, saveStockAsset, searchStock } from '@/data/sources-media';
import type { MediaKind, Orientation, SavedAsset, StockItem } from '@/types/media';
import { OwnMediaPicker } from './OwnMediaPicker';
import { assetDisplayName, selectOwnAsset } from './ownMediaModel';

/* AI Content Studio — media picker. Two tabs over ONE placement contract:
 *   • "Stock library"  — Pexels search + save (unchanged v0 behaviour).
 *   • "My uploads"     — the workspace's OWN uploaded media (P0-B).
 * The browser only ever calls the BC endpoints (never a provider directly) and
 * only ever sees this workspace's own assets. Selection from EITHER tab is
 * surfaced through the SAME `onSaved(item, savedId)` contract, so the host
 * (studio) consumes it unchanged. */

const ORIENTATIONS: Array<{ value: Orientation | 'any'; label: string }> = [
  { value: 'any', label: 'Any orientation' },
  { value: 'landscape', label: 'Landscape' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'square', label: 'Square' },
];

const inputClass =
  'h-control w-full rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) {
      const retry = (err.details as { retry_after?: number } | undefined)?.retry_after;
      return retry
        ? `The stock media service is rate-limiting requests. Try again in about ${retry}s.`
        : 'The stock media service is rate-limiting requests. Please try again shortly.';
    }
    if (err.status === 503) return 'Stock media is not configured yet. An admin needs to finish setup.';
    if (err.status === 502) return 'The stock media service returned an unexpected response. Try again.';
    if (err.status === 403) return 'Saving stock media is not available right now. Refresh the page and try again.';
    return err.message || 'The stock media service returned an unexpected response.';
  }
  return err instanceof Error ? err.message : 'Unexpected error. Try again.';
}

export interface StockMediaPickerProps {
  /** Called with the saved asset id + item after a successful save/selection
   *  (stock save OR own-media selection). Unchanged contract. */
  onSaved?: (item: StockItem, savedId: number | null) => void;
}

type PickerTab = 'stock' | 'uploads' | 'saved';

export function StockMediaPicker({ onSaved }: StockMediaPickerProps) {
  const [tab, setTab] = useState<PickerTab>('stock');
  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-md border border-border-default p-0.5" role="tablist" aria-label="Media source">
        {([
          ['stock', 'Stock library'],
          ['uploads', 'My uploads'],
          ['saved', 'Saved to workspace'],
        ] as Array<[PickerTab, string]>).map(([value, label]) => (
          <button
            key={value}
            role="tab"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
            className={
              'rounded-[5px] px-3 py-1 text-sm font-medium transition-colors ' +
              (tab === value ? 'bg-ai text-white' : 'text-text-secondary hover:text-text-primary')
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'stock' ? (
        <StockTab onSaved={onSaved} />
      ) : tab === 'uploads' ? (
        <OwnMediaPicker onSelect={(asset, previewUrl) => selectOwnAsset(asset, previewUrl, { onSaved })} />
      ) : (
        <SavedAssetsTab onSelect={(asset, previewUrl) => selectOwnAsset(asset, previewUrl, { onSaved })} />
      )}
    </div>
  );
}

/* Stock (Pexels) search + save — v0 behaviour, unchanged. */
function StockTab({ onSaved }: StockMediaPickerProps) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<MediaKind>('photo');
  const [draft, setDraft] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [page, setPage] = useState(1);
  const [orientation, setOrientation] = useState<Orientation | 'any'>('any');
  const [savedIds, setSavedIds] = useState<Record<string, boolean>>({});

  const search = useQuery({
    queryKey: ['stock-search', kind, submitted, page, orientation],
    queryFn: () =>
      searchStock({
        query: submitted,
        kind,
        page,
        orientation: orientation === 'any' ? null : orientation,
      }),
    enabled: submitted.trim() !== '',
  });

  const saveMutation = useMutation({
    mutationFn: (item: StockItem) => saveStockAsset(item, defaultRendition(item)),
    onSuccess: (res, item) => {
      setSavedIds((prev) => ({ ...prev, [item.providerAssetId]: true }));
      void queryClient.invalidateQueries({ queryKey: ['saved-media'] });
      onSaved?.(item, res.asset.id);
    },
  });

  const runSearch = (nextKind?: MediaKind) => {
    if (nextKind && nextKind !== kind) setKind(nextKind);
    setPage(1);
    setSubmitted(draft);
  };

  const items = search.data?.items ?? [];
  const totalLabel = useMemo(() => {
    if (!search.data) return '';
    const total = search.data.totalResults;
    return total > 0 ? `${total.toLocaleString('en-GB')} results` : '';
  }, [search.data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-border-default p-0.5" role="tablist" aria-label="Media type">
          {(['photo', 'video'] as MediaKind[]).map((k) => (
            <button
              key={k}
              role="tab"
              aria-selected={kind === k}
              onClick={() => runSearch(k)}
              className={
                'rounded-[5px] px-3 py-1 text-sm font-medium transition-colors ' +
                (kind === k ? 'bg-ai text-white' : 'text-text-secondary hover:text-text-primary')
              }
            >
              {k === 'photo' ? 'Photos' : 'Videos'}
            </button>
          ))}
        </div>
        <div className="flex min-w-[220px] flex-1 items-center gap-2">
          <input
            className={inputClass}
            placeholder={`Search ${kind === 'photo' ? 'photos' : 'videos'}…`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runSearch();
            }}
            aria-label="Search query"
          />
        </div>
        <select
          className={inputClass + ' w-auto'}
          value={orientation}
          onChange={(e) => {
            setOrientation(e.target.value as Orientation | 'any');
            setPage(1);
          }}
          aria-label="Orientation"
        >
          {ORIENTATIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <Button variant="ai" onClick={() => runSearch()} disabled={draft.trim() === ''}>
          Search
        </Button>
      </div>

      <p className="text-xs text-text-secondary">
        Search photos and videos for your content. Saved assets are stored in this workspace.
      </p>

      {submitted.trim() === '' ? (
        <EmptyState
          title="Search stock media"
          teach="Enter a term to browse photos or videos. Save one to use it in this workspace."
        />
      ) : search.isLoading ? (
        <LoadingState rows={3} />
      ) : search.isError ? (
        <ErrorState error={describeError(search.error)} onRetry={() => void search.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No results"
          teach="Nothing matched that search. Try a different term or switch between photos and videos."
        />
      ) : (
        <>
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-secondary">{totalLabel}</span>
            <span className="text-xs text-text-secondary">Page {search.data?.page ?? page}</span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
              <ResultCard
                key={`${item.kind}-${item.providerAssetId}`}
                item={item}
                saved={!!savedIds[item.providerAssetId]}
                saving={saveMutation.isPending && saveMutation.variables?.providerAssetId === item.providerAssetId}
                onSave={() => saveMutation.mutate(item)}
              />
            ))}
          </div>
          {saveMutation.isError && (
            <p className="text-sm text-danger" role="alert">
              {describeError(saveMutation.error)}
            </p>
          )}
          <div className="flex items-center justify-center gap-3 pt-2">
            <Button variant="secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
              Previous
            </Button>
            <Button variant="secondary" onClick={() => setPage((p) => p + 1)} disabled={!search.data?.nextPage}>
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function ResultCard({
  item,
  saved,
  saving,
  onSave,
}: {
  item: StockItem;
  saved: boolean;
  saving: boolean;
  onSave: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const isVideo = item.kind === 'video';
  const canPreview = isVideo && !!item.previewVideoUrl;
  return (
    <Card className="flex flex-col overflow-hidden">
      <div
        className="relative aspect-[4/3] bg-inset"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {item.previewUrl ? (
          <img src={item.previewUrl} alt={item.alt ?? 'Stock media preview'} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-text-secondary">No preview</div>
        )}
        {canPreview && hovered && (
          <video
            src={item.previewVideoUrl ?? undefined}
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            aria-hidden="true"
          />
        )}
        {isVideo && (
          <span className="pointer-events-none absolute left-2 top-2">
            <Badge tone="ai">Video{item.durationSecs ? ` · ${item.durationSecs}s` : ''}</Badge>
          </span>
        )}
      </div>
      <div className="flex items-center justify-end p-3">
        {saved ? (
          <Badge tone="success">Saved</Badge>
        ) : (
          <Button variant="secondary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>
    </Card>
  );
}

/* "Saved to workspace" tab — reuse ANY already-saved workspace asset (stock OR
 * own upload) WITHOUT a second external search+save cycle. Reads the SAME
 * source as the standalone Stock Library (GET /media/assets) and places the
 * selection through the identical onSaved contract. Server-scoped: only this
 * workspace's assets are ever returned. */
function SavedAssetsTab({ onSelect }: { onSelect: (asset: SavedAsset, previewUrl: string | null) => void }) {
  const saved = useQuery({ queryKey: ['saved-media'], queryFn: fetchSavedAssets });
  const items = saved.data?.items ?? [];
  return (
    <div className="space-y-3">
      <p className="text-xs text-text-secondary">Reuse a photo or video you already saved to this workspace — no new search needed.</p>
      {saved.isLoading ? (
        <LoadingState rows={3} />
      ) : saved.isError ? (
        <ErrorState error="Could not load saved media. Try again." onRetry={() => void saved.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title="Nothing saved yet" teach="Save a photo/video from the Stock library tab, or upload your own in My uploads — saved assets appear here for reuse." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((asset) => {
            const isVideo = asset.kind === 'video';
            return (
              <Card key={asset.id ?? `${asset.providerAssetId}-${asset.rendition}`} className="flex flex-col overflow-hidden">
                <div className="relative aspect-[4/3] bg-inset">
                  {asset.previewUrl ? (
                    isVideo ? (
                      <video src={asset.previewUrl} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                    ) : (
                      <img src={asset.previewUrl} alt={assetDisplayName(asset)} loading="lazy" className="h-full w-full object-cover" />
                    )
                  ) : (
                    <div className="flex h-full items-center justify-center px-2 text-center text-xs text-text-secondary">{assetDisplayName(asset)}</div>
                  )}
                  {isVideo && <span className="pointer-events-none absolute left-2 top-2"><Badge tone="ai">Video</Badge></span>}
                </div>
                <div className="flex items-center justify-between gap-2 p-3">
                  <span className="min-w-0 flex-1 truncate text-xs text-text-secondary" title={assetDisplayName(asset)}>{assetDisplayName(asset)}</span>
                  <Button variant="secondary" onClick={() => onSelect(asset, asset.previewUrl)}>Use</Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
